import { TD } from '@/components/DesignLibrary/Table';
import ProgressIndicator from '@/components/ProgressIndicator';
import UserImage from '@/components/UserImage';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { useCallback, useEffect, useRef, useState } from 'react';
import RenderText from '@/components/Message/RenderText';
import Button from '@/components/DesignLibrary/Button';
import { formatDistance } from 'date-fns';
import AddUserWorkflowNoteModal from '@/modals/AddUserWorkflowNoteModal';
import Note from '@/components/Icons/Note';
import Spacer from '@/components/Spacer';

import s from './Note.module.scss';

export type Props = {
  type: 'note';
  userWorkflowId: string;
};

const NotesCell: React.FC<Props> = ({ userWorkflowId }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [exceedsHeight, setExceedsHeight] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [note, setNote] = useState<Awaited<ReturnType<typeof tsClient.workflows.createUserWorkflowNote>> | null>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const { data, fetchingState, getData } = useNoodleApi(tsClient.workflows.getUserWorkflowNotes);
  const {
    data: collaborators,
    fetchingState: collaboratorsFetchingState,
    getData: getCollaboratorsFn,
  } = useNoodleApi(tsClient.workflows.getUserWorkflowCollaborators);

  const getLatestCollaborators = useCallback((): void => {
    getCollaboratorsFn({ userWorkflowId });
  }, [getCollaboratorsFn, userWorkflowId]);

  useEffect(() => {
    getData({ page: 1, perPage: 1, userWorkflowId });
    getLatestCollaborators();
  }, [getData, getLatestCollaborators, userWorkflowId]);

  useEffect(() => {
    if (data?.items?.length && messageRef.current) {
      const messageHeight = messageRef.current.scrollHeight;
      setExceedsHeight(messageHeight > 36);
    }
  }, [data, note]);

  useEffect(() => {
    if (data?.items) {
      setNote(data.items[0]);
    }
  }, [data?.items]);

  const addNote = async (comment: Awaited<ReturnType<typeof tsClient.workflows.createUserWorkflowNote>>): Promise<void> => {
    setNote(comment);
  };

  if (fetchingState.isFetching || collaboratorsFetchingState.isFetching) {
    return (
      <TD compact>
        <ProgressIndicator size={16} />
      </TD>
    );
  }

  if (!collaborators?.length) {
    return <TD compact />;
  }

  if (!note) {
    return (
      <>
        <TD compact>
          <Button variant="secondary" size="xs" onClick={() => setIsNoteModalOpen(true)}>
            <Note weight='fill' style={{ marginRight: 4 }} /> Add note
          </Button>
        </TD>
        {isNoteModalOpen && (
          <AddUserWorkflowNoteModal
            collaborators={collaborators}
            onClose={() => setIsNoteModalOpen(false)}
            userWorkflowId={userWorkflowId}
            onAddComment={addNote}
          />
        )}
      </>
    );
  }

  const { owner, createdAt } = note;

  return (
    <>
      <TD compact>
        <div ref={topRef} style={{ position: 'absolute', top: -64 }} /> {/* scroll back to cell on collapse helper */}
        <div className={s.container}>
          {owner && (
            <div className={s.name}>
              <UserImage color={owner.primaryColour?.hex} url={owner.image?.url} name={owner.name} size={14} />
              <p className="caption">{owner.name}</p>
            </div>
          )}
          <div ref={messageRef} className={s.message} style={{ maxHeight: collapsed ? '36px' : 'none' }}>
            <RenderText comment={note} />
          </div>
          {exceedsHeight && (
            <Button
              variant="secondary"
              size="xs"
              onClick={() => {
                setCollapsed(!collapsed);
                if (topRef.current && !collapsed) {
                  topRef.current.scrollIntoView();
                }
              }}
              className={s.button}
            >
              {collapsed ? 'Show more' : 'Show less'}
            </Button>
          )}
          <p className={s.date}>Added {formatDistance(new Date(createdAt), new Date())} ago</p>
        </div>
        <Spacer isLine size={8} />
        <Button variant="secondary" size="xs" onClick={() => setIsNoteModalOpen(true)}>
          <Note weight='fill' style={{ marginRight: 4 }} /> Add another note
        </Button>
      </TD>
      {isNoteModalOpen && (
        <AddUserWorkflowNoteModal
          collaborators={collaborators}
          onClose={() => setIsNoteModalOpen(false)}
          userWorkflowId={userWorkflowId}
          onAddComment={addNote}
        />
      )}
    </>
  );
};

export default NotesCell;
